/**
 * 詳細フォームのプリフィル元セレクタ。
 *
 * 先頭に「テンプレート」カード（ロールマスタの雛形 = 既定）、続けて
 * 「同じ相手 × 同じロール名で過去に送信したオファー」のカードを横スクロールで
 * 並べる。カードを選ぶと `onSelect` が呼ばれ、`DetailFormStep` 側で
 * フォームの内容系フィールドが選択元の値で上書きされる。
 *
 * 履歴が 0 件のときは呼び出し側でセクションごと非表示にする想定
 * （このコンポーネントはテンプレートカード単独では意味を持たないため）。
 */
import React from "react";
import styled from "styled-components";

import type {
  DmThreadStatus,
  OfferHistoryItem,
} from "../../../app/services/dmThreads";
import { formatYen } from "../../../utils/money";
import { addConsumptionTax } from "../../../utils/tax";
import type { OfferPrefillSource } from "../types";

interface PrefillSourcePickerProps {
  /** 過去オファー履歴（新しい順） */
  historyItems: OfferHistoryItem[];
  /** 現在選択中のプリフィル元 */
  selected: OfferPrefillSource;
  /** カード選択時に呼ばれる */
  onSelect: (source: OfferPrefillSource) => void;
}

// 履歴カードに出す委託者視点のステータスラベル（ThreadItem の表記を簡約）
const HISTORY_STATUS_LABELS: Partial<Record<DmThreadStatus, string>> = {
  contractor_pending: "承認待ち",
  estimating: "お見積待ち",
  estimate_review: "お見積審査中",
  in_progress: "進行中",
  inspection_pending: "検収待ち",
  completed: "完了",
  rejected: "不成立",
  closed: "クローズ",
};

const CardRow = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 4px;
  margin: -4px;
  /* カードの outline がスクロールコンテナで切れないよう padding で逃がす */
  -webkit-overflow-scrolling: touch;
`;

const SourceCard = styled.button<{ $selected: boolean }>`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 168px;
  padding: 12px;
  text-align: left;
  background: ${({ $selected }) =>
    $selected ? "var(--soremo-background, #f7f7f7)" : "#fff"};
  border: 1px solid
    ${({ $selected }) =>
      $selected ? "var(--soremo-blue)" : "var(--soremo-border)"};
  border-radius: 8px;
  cursor: pointer;
`;

const CardTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  .material-symbols-rounded {
    font-size: 16px;
    color: var(--soremo-light-gray);
  }
`;

const CardTitle = styled.span.attrs({ className: "heading-13" })`
  color: var(--soremo-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CardMeta = styled.span.attrs({ className: "bodytext-11" })`
  color: var(--soremo-light-gray);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CardAmount = styled.span.attrs({ className: "bodytext-13" })`
  color: var(--soremo-text);
`;

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(
    d.getDate(),
  ).padStart(2, "0")}`;
};

const PrefillSourcePicker: React.FC<PrefillSourcePickerProps> = ({
  historyItems,
  selected,
  onSelect,
}) => {
  const isTemplateSelected = selected.kind === "template";

  return (
    <CardRow role="radiogroup" aria-label="入力内容のベース">
      <SourceCard
        type="button"
        role="radio"
        aria-checked={isTemplateSelected}
        $selected={isTemplateSelected}
        onClick={() => onSelect({ kind: "template" })}
      >
        <CardTitleRow>
          <span className="material-symbols-rounded">description</span>
          <CardTitle>テンプレート</CardTitle>
        </CardTitleRow>
        <CardMeta>ロールの雛形から入力</CardMeta>
      </SourceCard>

      {historyItems.map((item) => {
        const isSelected =
          selected.kind === "history" && selected.dmId === item.dm_id;
        const statusLabel = HISTORY_STATUS_LABELS[item.status] ?? "";
        return (
          <SourceCard
            key={item.dm_id}
            type="button"
            role="radio"
            aria-checked={isSelected}
            $selected={isSelected}
            onClick={() => onSelect({ kind: "history", dmId: item.dm_id })}
          >
            <CardTitleRow>
              <span className="material-symbols-rounded">history</span>
              <CardTitle>{formatDate(item.created_at)}</CardTitle>
            </CardTitleRow>
            <CardMeta>
              {item.project.name ?? ""}
              {statusLabel && ` ・ ${statusLabel}`}
            </CardMeta>
            {item.contract_amount !== null && (
              <CardAmount>
                {/* contract_amount（= OfferCreator.reward）は税抜で保持される
                    ため、進行中一覧などと同じく FE 側で税込に変換して表示する */}
                {formatYen(addConsumptionTax(item.contract_amount))}
                <CardMeta>（税込）</CardMeta>
              </CardAmount>
            )}
          </SourceCard>
        );
      })}
    </CardRow>
  );
};

export default PrefillSourcePicker;
